import React from 'react';
import moment from 'moment';
import { pathOr } from 'ramda';
import { FormattedMessage } from 'react-intl';

import { disabledStatuses, enabledStatuses } from './constants';
import { formatCurrency } from '../../utils';

/** The same condition Unit Affordable Setup uses to decide whether to render. */
export const floorPlanHasAnyAffordableProgram = (floorPlan: ?Object) =>
  (floorPlan?.floorPlanAffordablePrograms ?? []).some((fpap) =>
    Boolean(fpap?.propertyAffordableProgram?.masterAffordableProgram?.name),
  );

/**
 * Alternative Unit Name (XML) has exactly one home per unit, and these two helpers
 * are the only place that decides which (ENG-30786). Unit Affordable Setup owns it
 * whenever that card renders; only market units fall back to General Unit
 * Information, and only at an affordable/mixed property. A unit with no floor plan
 * has not loaded yet, so neither section claims the field.
 */
export const showsXmlUnitNumberInAffordableSetup = ({
  floorPlan,
  affUnitDetailsEnabled,
}: {
  floorPlan: ?Object,
  affUnitDetailsEnabled: ?boolean,
}) =>
  Boolean(affUnitDetailsEnabled) && floorPlanHasAnyAffordableProgram(floorPlan);

export const showsXmlUnitNumberInGeneralUnitInformation = ({
  isAffordableProperty,
  floorPlan,
  affUnitDetailsEnabled,
}: {
  isAffordableProperty: ?boolean,
  floorPlan: ?Object,
  affUnitDetailsEnabled: ?boolean,
}) =>
  Boolean(isAffordableProperty) &&
  Boolean(floorPlan) &&
  !showsXmlUnitNumberInAffordableSetup({ floorPlan, affUnitDetailsEnabled });

export const assignHideUnitFromOnlineAvailabilityToUnit = ({
  unitStatuses,
  unit,
}: {
  unitStatuses: Array<Object>,
  unit: Object,
}) => {
  const updatedUnit = { ...unit };
  const selectedUnitStatus = unitStatuses?.find((unitStatus) => {
    return unitStatus?.id === unit?.unitStatusId;
  });

  if (enabledStatuses.includes(selectedUnitStatus?.description)) {
    updatedUnit.hideUnitFromOnlineAvailability = false;
  }

  if (disabledStatuses.includes(selectedUnitStatus?.description)) {
    updatedUnit.hideUnitFromOnlineAvailability = true;
  }

  return updatedUnit;
};

export const formatAmenities = (
  intl: any,
  amenities: Array<Object>,
  variation: boolean = false,
) => {
  return amenities.map((amenity) => {
    const name = pathOr('', ['propertyFees', 'displayNameOnQuote'], amenity);
    const includeInCalculation = pathOr(
      false,
      ['propertyFees', 'includeInMarketRentCalculation'],
      amenity,
    );
    const feeAmount = pathOr(
      pathOr(0, ['propertyFees', 'feeAmount']),
      ['feeAmount'],
      amenity,
    );

    if (includeInCalculation) {
      const formattedFeeAmount = formatCurrency(
        intl,
        parseFloat(feeAmount),
        true,
      );
      return variation
        ? { name, formattedFeeAmount, feeAmount }
        : `${name} - (${formattedFeeAmount}) included in Market Rent and Quoting Rent`;
    } else {
      return variation ? { name } : name;
    }
  });
};

export const formatMoveOutDateWithTransfer = ({
  moveOutDate,
  isTransfer,
  intl,
  messages,
}) => {
  if (!moveOutDate) {
    return '---';
  }

  const formattedDate = intl.formatDate(moment(moveOutDate), {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });

  if (!isTransfer) {
    return formattedDate;
  }

  const transferIndicator = intl.formatMessage(messages.transfer);
  return `${formattedDate} ${transferIndicator}`;
};

/**
 * Formats a move-out date with transfer indicator for JSX components
 * @param {Object} params - Parameters object
 * @param {number|string|null} params.moveOutDate - The move-out date
 * @param {boolean} params.isTransfer - Whether this move-out was due to a transfer
 * @param {Object} params.intl - Internationalization object
 * @param {Object} params.messages - Message definitions object
 * @returns {React.Element} JSX element with formatted date and transfer indicator
 */
export const formatMoveOutDateWithTransferJSX = ({
  moveOutDate,
  isTransfer,
  intl,
  messages,
}) => {
  if (!moveOutDate) {
    return <span>---</span>;
  }

  const formattedDate = intl.formatDate(moment(moveOutDate), {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });

  if (!isTransfer) {
    return <span>{formattedDate}</span>;
  }

  return (
    <span>
      {formattedDate} <FormattedMessage {...messages.transfer} />
    </span>
  );
};
